import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../services/apiClient';
import { CampaignRow, emailService } from '../services/emailService';
import { useToast } from '../contexts/ToastContext';

const CampaignDetail: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!campaignId) return;
    let mounted = true;
    (async () => {
      try {
        const row = await emailService.getCampaign(campaignId);
        if (!mounted) return;
        setCampaign(row);
      } catch (err) {
        if (!mounted) return;
        const message = err instanceof ApiError ? err.message : 'Failed to load campaign.';
        setError(message);
        showToast(message, 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [campaignId, showToast]);

  const handleCancel = async () => {
    if (!campaignId || !campaign) return;
    const confirmed = window.confirm('Cancel this outreach campaign?');
    if (!confirmed) return;
    setActionLoading(true);
    try {
      const updated = await emailService.cancelCampaign(campaignId);
      setCampaign(updated);
      showToast('Campaign cancelled.', 'success');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to cancel campaign.';
      setError(message);
      showToast(message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <p className="text-sm text-fg-muted">Loading campaign…</p>;
  if (!campaign) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600 dark:text-red-400">{error || 'Campaign not found.'}</p>
        <Link to="/campaigns" className="btn-secondary text-sm">
          Back to Campaigns
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-fg">{campaign.name}</h1>
          <p className="text-sm text-fg-muted">Status: {campaign.status}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => navigate('/campaigns')} className="btn-secondary text-sm">
            Back
          </button>
          {(campaign.status === 'draft' || campaign.status === 'queued') && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={actionLoading}
              className="rounded-xl border border-border px-3 py-2 text-sm text-fg disabled:opacity-50"
            >
              {actionLoading ? 'Cancelling…' : 'Cancel campaign'}
            </button>
          )}
        </div>
      </div>

      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border p-3">
          <p className="text-xs text-fg-muted">Total selected</p>
          <p className="text-lg font-semibold text-fg">{campaign.total_selected}</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-xs text-fg-muted">Sent</p>
          <p className="text-lg font-semibold text-fg">{campaign.sent_count}</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-xs text-fg-muted">Failed</p>
          <p className="text-lg font-semibold text-fg">{campaign.failed_count}</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-xs text-fg-muted">Skipped</p>
          <p className="text-lg font-semibold text-fg">{campaign.skipped_count}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-2">
        <p className="text-sm font-medium text-fg">Subject</p>
        <p className="text-sm text-fg-muted">{campaign.subject}</p>
        <p className="text-sm font-medium text-fg pt-2">Body template</p>
        <pre className="whitespace-pre-wrap text-xs text-fg-muted">{campaign.body_template}</pre>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-muted/80">
            <tr>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-muted">Email</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-muted">Status</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-muted">Failure reason</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-fg-muted">Sent at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {(campaign.recipients || []).map((recipient) => (
              <tr key={recipient.id}>
                <td className="px-4 py-3 text-sm text-fg">{recipient.email || '—'}</td>
                <td className="px-4 py-3 text-sm text-fg-muted">{recipient.status}</td>
                <td className="px-4 py-3 text-sm text-fg-muted">{recipient.failure_reason || '—'}</td>
                <td className="px-4 py-3 text-sm text-fg-muted">
                  {recipient.sent_at ? new Date(recipient.sent_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
            {(campaign.recipients || []).length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-sm text-fg-muted" colSpan={4}>
                  No recipients recorded for this campaign yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignDetail;
